import React, { useEffect, useState } from "react";
import { Button, Modal, message, Select, Tag } from "antd";
import { Form, InputGroup, Badge } from "react-bootstrap";
import { PlusOutlined, EditOutlined, DeleteOutlined, PhoneOutlined, CheckOutlined, CloseOutlined } from "@ant-design/icons";
import { oneKlicks } from "../../SDK/oneclick";
import { getuserdata } from "../../services/apiService";
import Paginationnew from "../../Uitiles/Paginationnew";

const BloodRequests = () => {
    const [requests, setRequests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [modalVisible, setModalVisible] = useState(false);
    const [editingRequest, setEditingRequest] = useState(null);
    const [searchText, setSearchText] = useState("");
    const [page, setPage] = useState(1);
    const [limit] = useState(10);
    const [totalPages, setTotalPages] = useState(0);

    // Form states
    const [formData, setFormData] = useState({
        patient_name: '',
        blood_group: '',
        units_required: 1,
        hospital_name: '',
        contact_phone: '',
        urgency: 'normal',
        status: 'pending',
        notes: ''
    });

    const bloodGroups = ['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'];
    const urgencyOptions = ['normal', 'urgent', 'critical'];
    const statusOptions = ['pending', 'approved', 'fulfilled', 'rejected'];

    useEffect(() => {
        loadRequests();
    }, [page, searchText]);

    const loadRequests = async () => {
        try {
            setLoading(true);
            const userId = await getuserdata('id');
            const conditions = { blood_bank_id: userId };
            const search = searchText ? {
                patient_name: searchText,
                blood_group: searchText,
                hospital_name: searchText,
                contact_phone: searchText
            } : {};
            const pagination = { page, limit };

            const response = await oneKlicks.read('', 'blood_requests', conditions, 'id DESC', search, pagination);
            if (response && response.data) {
                setRequests(response.data);
                setTotalPages(response.totalPages || 1);
            }
        } catch (error) {
            console.error('Error loading requests:', error);
            message.error('Failed to load requests');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async () => {
        try {
            if (!formData.patient_name || !formData.blood_group || !formData.contact_phone) {
                message.error('Please fill in all required fields');
                return;
            }

            const userId = await getuserdata('id');
            const requestData = {
                ...formData,
                blood_bank_id: userId,
                request_date: new Date().toISOString().split('T')[0]
            };

            if (editingRequest) {
                await oneKlicks.update('', 'blood_requests', requestData, { id: editingRequest.id });
                message.success('Request updated successfully');
            } else {
                await oneKlicks.create('', 'blood_requests', requestData);
                message.success('Request added successfully');
            }

            setModalVisible(false);
            resetForm();
            loadRequests();
        } catch (error) {
            console.error('Error saving request:', error);
            message.error('Failed to save request');
        }
    };

    const resetForm = () => {
        setFormData({
            patient_name: '',
            blood_group: '',
            units_required: 1,
            hospital_name: '',
            contact_phone: '',
            urgency: 'normal',
            status: 'pending',
            notes: ''
        });
        setEditingRequest(null);
    };

    const handleEdit = (request) => {
        setEditingRequest(request);
        setFormData({
            patient_name: request.patient_name || '',
            blood_group: request.blood_group || '',
            units_required: request.units_required || 1,
            hospital_name: request.hospital_name || '',
            contact_phone: request.contact_phone || '',
            urgency: request.urgency || 'normal',
            status: request.status || 'pending',
            notes: request.notes || ''
        });
        setModalVisible(true);
    };

    const handleDelete = async (request) => {
        const confirmDelete = window.confirm(`Are you sure you want to delete this request for "${request.patient_name}"?`);
        if (confirmDelete) {
            try {
                await oneKlicks.delete('', 'blood_requests', { id: request.id });
                message.success('Request deleted successfully');
                loadRequests();
            } catch (error) {
                console.error('Error deleting request:', error);
                message.error('Failed to delete request');
            }
        }
    };

    const handleStatusChange = async (request, newStatus) => {
        try {
            await oneKlicks.update('', 'blood_requests', { status: newStatus }, { id: request.id });
            message.success(`Status changed to ${newStatus}`);
            loadRequests();
        } catch (error) {
            console.error('Error updating status:', error);
            message.error('Failed to update status');
        }
    };

    const handlePageChange = (newPage) => {
        setPage(newPage);
    };

    const getBloodGroupBadge = (group) => {
        const colors = {
            'A+': 'danger', 'A-': 'danger', 'B+': 'primary', 'B-': 'primary',
            'AB+': 'secondary', 'AB-': 'secondary', 'O+': 'success', 'O-': 'success'
        };
        return colors[group] || 'info';
    };

    const getStatusColor = (status) => {
        const colors = {
            pending: 'orange',
            approved: 'blue',
            fulfilled: 'green',
            rejected: 'red'
        };
        return colors[status] || 'default';
    };

    const getUrgencyColor = (urgency) => {
        const colors = {
            normal: 'default',
            urgent: 'orange',
            critical: 'red'
        };
        return colors[urgency] || 'default';
    };

    return (
        <>
            <div className="container-fluid py-3">
                <div className="card mb-5">
                    <div className="card-body">
                        <div className="align-items-center justify-content-between mb-3">
                            <div className="d-flex align-items-center gap-2 mb-2">
                                <h5 className="mb-0 font-weight-bold mr-3">Blood Requests Management</h5>
                                <Badge bg="warning" text="dark">Requests</Badge>
                            </div>
                            <div className="d-flex justify-content-between align-items-center" style={{ gap: 12 }}>
                                <InputGroup className="flex-grow-1 me-2 h-40">
                                    <Form.Control
                                        placeholder="Search by patient, blood group, hospital..."
                                        value={searchText}
                                        onChange={(e) => setSearchText(e.target.value)}
                                    />
                                </InputGroup>
                                <Button className="btn-primary h-40" onClick={() => {
                                    resetForm();
                                    setModalVisible(true);
                                }}>
                                    <PlusOutlined /> Add Request
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="card">
                    <div className="card-body">
                        <div className="table-responsive">
                            <table className="table table-hover mb-0 align-middle">
                                <thead>
                                    <tr>
                                        <th>ID</th>
                                        <th>Patient</th>
                                        <th>Blood Group</th>
                                        <th>Units</th>
                                        <th>Hospital</th>
                                        <th>Urgency</th>
                                        <th>Status</th>
                                        <th style={{ width: '200px' }}>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {loading ? (
                                        <tr>
                                            <td colSpan="8" className="text-center py-4">Loading...</td>
                                        </tr>
                                    ) : requests.length === 0 ? (
                                        <tr>
                                            <td colSpan="8" className="text-center py-4">No requests found</td>
                                        </tr>
                                    ) : (
                                        requests.map((item) => (
                                            <tr key={item.id}>
                                                <td data-label="ID">{item.id}</td>
                                                <td data-label="Patient">
                                                    <div>
                                                        <strong>{item.patient_name}</strong>
                                                        <div style={{ fontSize: '12px', color: '#666' }}>
                                                            <PhoneOutlined className="me-1" />{item.contact_phone}
                                                        </div>
                                                    </div>
                                                </td>
                                                <td data-label="Blood Group">
                                                    <Badge bg={getBloodGroupBadge(item.blood_group)}>
                                                        {item.blood_group}
                                                    </Badge>
                                                </td>
                                                <td data-label="Units">
                                                    <strong>{item.units_required}</strong> units
                                                </td>
                                                <td data-label="Hospital">
                                                    {item.hospital_name || '-'}
                                                </td>
                                                <td data-label="Urgency">
                                                    <Tag color={getUrgencyColor(item.urgency)}>
                                                        {item.urgency?.toUpperCase() || 'NORMAL'}
                                                    </Tag>
                                                </td>
                                                <td data-label="Status">
                                                    <Tag color={getStatusColor(item.status)}>
                                                        {item.status?.toUpperCase() || 'PENDING'}
                                                    </Tag>
                                                </td>
                                                <td data-label="Actions">
                                                    <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                                                        {item.status === 'pending' && (
                                                            <>
                                                                <Button
                                                                    className="btn-success"
                                                                    onClick={() => handleStatusChange(item, 'approved')}
                                                                    style={{ fontSize: '11px', padding: '4px 8px' }}
                                                                >
                                                                    <CheckOutlined />
                                                                </Button>
                                                                <Button
                                                                    className="btn-warning"
                                                                    onClick={() => handleStatusChange(item, 'rejected')}
                                                                    style={{ fontSize: '11px', padding: '4px 8px' }}
                                                                >
                                                                    <CloseOutlined />
                                                                </Button>
                                                            </>
                                                        )}
                                                        {item.status === 'approved' && (
                                                            <Button
                                                                className="btn-success"
                                                                onClick={() => handleStatusChange(item, 'fulfilled')}
                                                                style={{ fontSize: '11px', padding: '4px 8px' }}
                                                            >
                                                                Fulfill
                                                            </Button>
                                                        )}
                                                        <Button
                                                            className="btn-info"
                                                            onClick={() => handleEdit(item)}
                                                            style={{ fontSize: '11px', padding: '4px 8px' }}
                                                        >
                                                            <EditOutlined />
                                                        </Button>
                                                        <Button
                                                            className="btn-danger"
                                                            onClick={() => handleDelete(item)}
                                                            style={{ fontSize: '11px', padding: '4px 8px' }}
                                                        >
                                                            <DeleteOutlined />
                                                        </Button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                        <Paginationnew currentPage={page} totalPages={totalPages} handlePageChange={handlePageChange} />
                    </div>
                </div>

                {/* Add/Edit Modal */}
                <Modal
                    title={editingRequest ? 'Edit Blood Request' : 'Add New Blood Request'}
                    open={modalVisible}
                    onCancel={() => {
                        setModalVisible(false);
                        resetForm();
                    }}
                    onOk={handleSubmit}
                    width={700}
                >
                    <div className="row">
                        <div className="col-md-6 mb-3">
                            <label className="form-label">Patient Name *</label>
                            <Form.Control
                                placeholder="Enter patient name"
                                value={formData.patient_name}
                                onChange={(e) => setFormData({ ...formData, patient_name: e.target.value })}
                            />
                        </div>
                        <div className="col-md-6 mb-3">
                            <label className="form-label">Blood Group *</label>
                            <Select
                                style={{ width: '100%' }}
                                placeholder="Select blood group"
                                value={formData.blood_group || undefined}
                                onChange={(value) => setFormData({ ...formData, blood_group: value })}
                                options={bloodGroups.map(g => ({ value: g, label: g }))}
                            />
                        </div>
                        <div className="col-md-6 mb-3">
                            <label className="form-label">Units Required *</label>
                            <Form.Control
                                type="number"
                                min="1"
                                placeholder="Enter units required"
                                value={formData.units_required}
                                onChange={(e) => setFormData({ ...formData, units_required: parseInt(e.target.value) || 1 })}
                            />
                        </div>
                        <div className="col-md-6 mb-3">
                            <label className="form-label">Contact Phone *</label>
                            <Form.Control
                                placeholder="Enter contact phone"
                                value={formData.contact_phone}
                                onChange={(e) => setFormData({ ...formData, contact_phone: e.target.value })}
                            />
                        </div>
                        <div className="col-md-6 mb-3">
                            <label className="form-label">Hospital Name</label>
                            <Form.Control
                                placeholder="Enter hospital name"
                                value={formData.hospital_name}
                                onChange={(e) => setFormData({ ...formData, hospital_name: e.target.value })}
                            />
                        </div>
                        <div className="col-md-6 mb-3">
                            <label className="form-label">Urgency</label>
                            <Select
                                style={{ width: '100%' }}
                                value={formData.urgency}
                                onChange={(value) => setFormData({ ...formData, urgency: value })}
                                options={urgencyOptions.map(u => ({ value: u, label: u.charAt(0).toUpperCase() + u.slice(1) }))}
                            />
                        </div>
                        <div className="col-md-6 mb-3">
                            <label className="form-label">Status</label>
                            <Select
                                style={{ width: '100%' }}
                                value={formData.status}
                                onChange={(value) => setFormData({ ...formData, status: value })}
                                options={statusOptions.map(s => ({ value: s, label: s.charAt(0).toUpperCase() + s.slice(1) }))}
                            />
                        </div>
                        <div className="col-12 mb-3">
                            <label className="form-label">Notes</label>
                            <Form.Control
                                as="textarea"
                                rows={3}
                                placeholder="Additional notes..."
                                value={formData.notes}
                                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                            />
                        </div>
                    </div>
                </Modal>
            </div>
        </>
    );
};

export default BloodRequests;
